import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfig } from '../../../app.config';
import {
  ChangePasswordModel,
  CurrentUser,
  PasswordRecoveryModel,
  PasswordSecurity,
  ResetPasswordSecurityModel
} from './auth.model';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class AuthService {
  Isauthenticated = false;
  public currentUserSubject: BehaviorSubject<CurrentUser>;
  public currentUser: Observable<CurrentUser>;
  public versionNum: string;
  apiBaseUrl: string;
  constructor(private jwtHelper: JwtHelperService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private http: HttpClient,
    @Inject('BASE_URL') baseUrl: string
  ) {
    this.currentUserSubject = new BehaviorSubject<CurrentUser>(
      this.localStorageService.getItem('currentUser')
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.apiBaseUrl = baseUrl;
  }

  isAuthenticated(): boolean {

    const token = this.localStorageService.getItem('accesstoken');
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.Isauthenticated = this.currentUserValue
        ? this.currentUserValue.authenticated
        : false;
    }
    else {
      this.logout();
      this.Isauthenticated = false;
    }
    return this.Isauthenticated;
  }

  public get currentUserValue(): CurrentUser {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string): Observable<any> {
    const browserName: string = this.getBrowserNameAndVersion().name;
    const browserVersion: string = this.getBrowserNameAndVersion().version;
    if (!browserName || !browserVersion) {
      return;
    }
    var data = {
      Username: email,
      Password: password,
      BrowserName: browserName,
      BrowserVersion: browserVersion,
    };
    var reqHeader = new HttpHeaders({ 'Content-Type': 'application/json', 'No-Auth': 'True' });
    return this.http.post<any>(AppConfig.settings.settings.apiEndpoint + 'api/auth/Login', data, {
      headers: reqHeader,
    });
  }

  revokeToken() {
    const accessToken: string = this.localStorageService.getItem('accesstoken');
    const refreshToken: string = this.localStorageService.getItem('refreshtoken');
    const browserName: string = this.getBrowserNameAndVersion().name;
    const browserVersion: string = this.getBrowserNameAndVersion().version;
    if (!accessToken || !refreshToken || !browserName || !browserVersion) {
      return;
    }
    var data = {
      AccessToken: accessToken,
      RefreshToken: refreshToken,
      BrowserName: browserName,
      BrowserVersion: browserVersion,
    };
    var reqHeader = new HttpHeaders({ 'Content-Type': 'application/json' });
    this.http
      .post<any>(AppConfig.settings.settings.apiEndpoint + 'api/auth/Revoke', data, {
        headers: reqHeader,
      })
      .subscribe(data => {
        // remove refreshtoken from local storage to log user out
        this.logout();
      });
  }

  refreshToken(): Observable<any> {
    const accessToken: string = this.localStorageService.getItem('accesstoken');
    const refreshToken: string = this.localStorageService.getItem('refreshtoken');
    const browserName: string = this.getBrowserNameAndVersion().name;
    const browserVersion: string = this.getBrowserNameAndVersion().version;
    if (accessToken && refreshToken && browserName && browserVersion) {
      var params = {
        AccessToken: accessToken,
        RefreshToken: refreshToken,
        BrowserName: browserName,
        BrowserVersion: browserVersion,
      };
      var reqHeader = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http
        .post<any>(AppConfig.settings.settings.apiEndpoint + 'api/auth/Refresh', params, {
          headers: reqHeader,
        })
        .pipe(
          map(data => {
            if (data) {
              this.localStorageService.setItem('accesstoken', data.accesstoken);
              this.localStorageService.setItem('refreshtoken', data.refreshtoken);
              this.startRefreshTokenTimer();
             }
            return data;
          })
        );
    }
  }

  logout() {
    this.localStorageService.removeItem('accesstoken');
    this.localStorageService.removeItem('currentUser');
    this.localStorageService.removeItem('refreshtoken');
    this.localStorageService.removeItem('apiEndpoint');
    this.localStorageService.removeItem('browserName');
    this.localStorageService.removeItem('browserVersion');
    this.Isauthenticated = false;
    this.stopRefreshTokenTimer();
    this.currentUserSubject.next(null);
  }

  userLogout(): Observable<any> {
    var user = {
      Username: this.localStorageService.getItem('currentUser').Username,
      Password: this.localStorageService.getItem('currentUser').password,
    };
    var reqHeader = new HttpHeaders({ 'Content-Type': 'application/json', 'No-Auth': 'True' });
    return this.http.post(AppConfig.settings.settings.apiEndpoint + 'api/auth/Logout', user, {
      headers: reqHeader,
    });
  }

  setTermandConditionAcceptanceDate(): Observable<any> {
    return this.http.post(
      AppConfig.settings.settings.apiEndpoint + 'api/auth/TermAcceptanceDateUtc',
      ''
    );
  }

  changePassword(entity: ChangePasswordModel): Observable<any> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Error-Message': 'True',
    });
    return this.http.post<ChangePasswordModel>(
      AppConfig.settings.settings.apiEndpoint + 'api/auth/ChangePassword',
      entity,
      { headers: reqHeader }
    );
  }

  getBrowserNameAndVersion() {
    var ua = navigator.userAgent,
      tem,
      M = ua.match(/(opera|chrome|safari|firefox|msie|edg|trident(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return { name: 'IE', version: tem[1] || '' };
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\bEdg\/(\d+)/);
      if (tem != null) {
        return { name: 'Edge(Chromium)', version: tem[1] };
      }
      tem = ua.match(/\bOPR\/(\d+)/);
      if (tem != null) {
        return { name: 'Opera', version: tem[1] };
      }
    }
    M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) {
      M.splice(1, 1, tem[1]);
    }
    return {
      name: M[0],
      version: M[1],
    };
  }

  getPasswordSecurityQuestions(): Observable<any> {
    return this.http.get<any>(
      `${AppConfig.settings.settings.apiEndpoint}api/Auth/PasswordSecurityQuestion`
    );
  }

  setPaswordSecurityAnswer(entity: PasswordSecurity): Observable<any> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Error-Message': 'True',
    });
    return this.http.post<PasswordSecurity>(
      `${AppConfig.settings.settings.apiEndpoint}api/auth/PasswordSecurityAnswer`,
      entity,
      { headers: reqHeader }
    );
  }

  forgotPassword(emailAddress: string, recoveryQuestion: PasswordRecoveryModel[]): Observable<boolean> {
    const entity = {
      emailAddress: emailAddress,
      clientURI: `${this.apiBaseUrl}#/resetpassword`,
      answerList: recoveryQuestion,
    };

    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Auth': 'True',
      'No-Error-Message': 'True',
    });

    return this.http.post<boolean>(
      `${AppConfig.settings.settings.apiEndpoint}api/auth/ForgotPassword`,
      entity,
      { headers: reqHeader }
    );
  }

  resetAuthPassword(entity: ResetPasswordSecurityModel): Observable<boolean> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Auth': 'True',
      'No-Error-Message': 'True',
    });

    return this.http.post<boolean>(
      `${AppConfig.settings.settings.apiEndpoint}api/auth/ResetPassword`,
      entity,
      { headers: reqHeader }
    );
  }

  checkEmail(email: string): Observable<any> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Auth': 'True',
      'No-Error-Message': 'True',
    });

    return this.http.post<any>(
      `${AppConfig.settings.settings.apiEndpoint}api/Auth/CheckEmail`,
      { email: email },
      { headers: reqHeader }
    );
  }

  getPasswordRecoveryQuestions(emailAddress: string): Observable<any> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Auth': 'True',
      'No-Error-Message': 'True',
    });

    return this.http.post<any>(
      `${AppConfig.settings.settings.apiEndpoint}api/Auth/PasswordRecoveryQuestion`,
      { email: emailAddress },
      { headers: reqHeader }
    );
  }

  verifyToken(entity: ResetPasswordSecurityModel): Observable<any> {
    var reqHeader = new HttpHeaders({
      'Content-Type': 'application/json',
      'No-Auth': 'True',
      'No-Error-Message': 'True',
    });

    return this.http.post<boolean>(
      `${AppConfig.settings.settings.apiEndpoint}api/auth/VerifyToken`,
      entity,
      { headers: reqHeader }
    );
  }

  // helper methods

  private refreshTokenTimeout?: ReturnType<typeof setTimeout>;

  public startRefreshTokenTimer() {
      // parse json object from base64 encoded jwt token
      const jwtBase64 = this.localStorageService.getItem('accesstoken');
      //const jwtToken = JSON.parse(atob(jwtBase64));

      // set a timeout to refresh the token a minute before it expires
      const expires = new Date(this.jwtHelper.decodeToken(jwtBase64).exp * 1000);
      const timeout = expires.getTime() - Date.now() + (4 * 60 * 1000);
      this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }

  private stopRefreshTokenTimer() {
      clearTimeout(this.refreshTokenTimeout);
  }
}
